import React, { useContext, useEffect, useState } from "react";
import { ReactReduxContext } from "react-redux";
import { useHistory } from "react-router-dom";
import {
    AppstoreOutlined,
    WalletOutlined,
    BarChartOutlined,
    MessageOutlined,
    SettingOutlined,
} from "@ant-design/icons";
import { Tokens } from "Shared/Tokens";

const NAV_TABS = [
    {
        key: "overview",
        label: "Overview",
        path: "/portfolios",
        icon: AppstoreOutlined,
    },
    {
        key: "binance",
        label: "Binance",
        path: "/imported/binance",
        icon: WalletOutlined,
    },
    {
        key: "report-card",
        label: "Summary",
        path: "/imported/binance/report-card",
        icon: BarChartOutlined,
    },
    {
        key: "ask-ai",
        label: "Ask AI",
        path: "/imported/binance/ai-news",
        icon: MessageOutlined,
    },
    {
        key: "settings",
        label: "Settings",
        path: "/settings",
        icon: SettingOutlined,
    },
];

export const AutoSyncBadge = () => {
    const reduxContext = useContext(ReactReduxContext);
    const [, forceUpdate] = useState(0);

    useEffect(() => {
        if (!reduxContext?.store?.subscribe) return undefined;
        return reduxContext.store.subscribe(() => {
            forceUpdate((n) => n + 1);
        });
    }, [reduxContext]);

    const state = reduxContext?.store?.getState?.() || {};
    const userInfo = state.users?.userInfo;
    const syncStatus = state.settings?.syncStatus || "idle";
    const lastBackup = state.settings?.lastBackup || 0;

    const isSyncing = Boolean(userInfo) && syncStatus === "syncing";
    const isError = syncStatus === "error";
    const isSynced =
        Boolean(userInfo) &&
        !isSyncing &&
        !isError &&
        (syncStatus === "synced" || lastBackup > 0);

    let syncState = "unsynced";
    let badgeTitle = "Not synced to cloud";
    let dotStyle = {
        display: "inline-block",
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        backgroundColor: "transparent",
        border: "1.5px solid #ffffff",
        boxShadow: "0 0 0 1px rgba(15, 23, 42, 0.35)",
        flexShrink: 0,
    };

    if (isSyncing) {
        syncState = "syncing";
        badgeTitle = "Automatically syncing changes to cloud...";
        dotStyle = {
            display: "inline-block",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: Tokens.positive,
            border: "none",
            animation: "pfSyncDotBlink 1s infinite ease-in-out",
            flexShrink: 0,
        };
    } else if (isSynced) {
        syncState = "synced";
        badgeTitle = "All changes automatically synced to cloud";
        dotStyle = {
            display: "inline-block",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: Tokens.positive,
            border: "none",
            flexShrink: 0,
        };
    } else if (isError) {
        syncState = "unsynced";
        badgeTitle = "Cloud sync pending retry";
    }

    return (
        <span
            data-testid="auto-sync-status-pill"
            data-sync-state={syncState}
            aria-label={badgeTitle}
            title={badgeTitle}
            className={
                isSyncing ? "pf-sync-dot pf-sync-dot--blinking" : "pf-sync-dot"
            }
            style={dotStyle}
        />
    );
};

export const BottomNavBar = ({ activeTab }) => {
    const history = useHistory();
    return (
        <nav
            data-testid="app-shell-bottom-nav"
            aria-label="Primary navigation"
            style={{
                backgroundColor: Tokens.card,
                borderTop: `1px solid ${Tokens.hairline}`,
                padding:
                    "6px 12px calc(6px + env(safe-area-inset-bottom, 0px))",
            }}
        >
            <div
                style={{
                    maxWidth: Tokens.containerMaxWidth,
                    margin: "0 auto",
                    display: "grid",
                    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                    gap: "4px",
                }}
            >
                {NAV_TABS.map((tab) => {
                    const IconComponent = tab.icon;
                    const isActive = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            data-testid={`nav-tab-${tab.key}`}
                            aria-current={isActive ? "page" : undefined}
                            onClick={() => {
                                if (
                                    history &&
                                    typeof history.push === "function"
                                ) {
                                    history.push(tab.path);
                                }
                            }}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "3px",
                                padding: "6px 4px",
                                borderRadius: Tokens.radiusControl,
                                border: "none",
                                backgroundColor: isActive
                                    ? Tokens.accentSubtle
                                    : "transparent",
                                color: isActive
                                    ? Tokens.accent
                                    : Tokens.textSecondary,
                                fontWeight: isActive ? 600 : 500,
                                fontSize: "11px",
                                cursor: "pointer",
                                transition:
                                    "background-color 150ms ease, color 150ms ease",
                            }}
                        >
                            <IconComponent style={{ fontSize: "16px" }} />
                            <span
                                style={{
                                    lineHeight: 1.1,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {tab.label}
                            </span>
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};

const AppShell = ({
    title,
    children,
    footer,
    headerStyle = {},
    bodyStyle = {},
    footerStyle = {},
    activeTab = null,
    showBottomNav = Boolean(activeTab),
}) => {
    let bottomOffset = "32px";
    if (footer && showBottomNav) {
        bottomOffset = "148px";
    } else if (footer || showBottomNav) {
        bottomOffset = "88px";
    }

    return (
        <div
            data-testid="app-shell"
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: Tokens.bg,
                color: Tokens.textPrimary,
                width: "100%",
            }}
        >
            {title && (
                <header
                    data-testid="app-shell-header"
                    style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 40,
                        backgroundColor: Tokens.bg,
                        borderBottom: `1px solid ${Tokens.hairline}`,
                        padding: "14px 16px",
                        ...headerStyle,
                    }}
                >
                    {title}
                </header>
            )}

            <main
                data-testid="app-shell-main"
                style={{
                    flex: 1,
                    backgroundColor: Tokens.bg,
                    padding: "20px 16px",
                    paddingBottom: bottomOffset,
                    width: "100%",
                    boxSizing: "border-box",
                    ...bodyStyle,
                }}
            >
                {children}
            </main>

            {(footer || showBottomNav) && (
                <div
                    style={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 40,
                        width: "100%",
                        backgroundColor: Tokens.card,
                        borderTop: `1px solid ${Tokens.hairline}`,
                    }}
                >
                    {footer && (
                        <div
                            data-testid="app-shell-footer"
                            style={{
                                backgroundColor: Tokens.card,
                                padding: "10px 16px",
                                textAlign: "left",
                                borderBottom: showBottomNav
                                    ? `1px solid ${Tokens.hairline}`
                                    : "none",
                                ...footerStyle,
                            }}
                        >
                            {footer}
                        </div>
                    )}

                    {showBottomNav && <BottomNavBar activeTab={activeTab} />}
                </div>
            )}
        </div>
    );
};

export default AppShell;
